import { logEvent } from '../lib/analytics'
import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { paletteFromPoster } from '../lib/nowPlaying'
import { Clapboard } from './ClapboardIcon'
import { ReelRating, REEL_LABELS } from './ReelRating'
import { useShareCard } from '../hooks/useShareCard'
import { rateKeepsake } from '../lib/keepsake'

// The Final Cut — the send-off for finishing a CONFIRMED-ENDED series all the way
// to its true final episode. v17 facelift (2026-09-15, from the Claude Design
// show-page mockup's Finale state): the show's tint floods the page over 600ms,
// "That's a wrap." rises after a beat, five reels ask for the series rating, and
// your run with the show sits beneath as a quiet mono block. No clapboard, no
// credits roll. Tap anywhere to leave. Fires from every completion path
// (useWatchlist dispatches nextep:finalcut) and honours the wrap hold/release
// contract (__nextepWrapUntil + nextep:wrap-done) so the held card can reveal.

function hoursLabel(minutes, episodes) {
  if (!minutes) return episodes ? (episodes + ' episodes') : null
  const h = Math.round(minutes / 60)
  if (h < 1) return minutes + ' minutes'
  return 'about ' + h + ' hour' + (h === 1 ? '' : 's')
}
function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch (e) { return null }
}

async function buildStats(show, user) {
  const segs    = show.seasons ? show.seasons.filter(s => s.season_number > 0) : []
  const seasons  = show.number_of_seasons || segs.length || 1
  const episodes = show.number_of_episodes || 0
  const runtime  = (show.episode_run_time && show.episode_run_time[0]) || 0
  const minutes  = episodes * runtime
  let started = null, finished = null, count = null
  try {
    if (user && user.id) {
      const res = await supabase.from('watchlist').select('added_at, completed_at').eq('user_id', user.id).eq('show_id', show.id).maybeSingle()
      if (res && res.data) { started = res.data.added_at; finished = res.data.completed_at }
      const cnt = await supabase.from('watchlist').select('show_id', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'completed')
      if (cnt && typeof cnt.count === 'number') count = cnt.count
    }
  } catch (err) { /* best-effort */ }
  return { seasons, episodes, minutes, started, finished, count }
}

export function FinalCut({ user, wl }) {
  const [data, setData]   = useState(null)
  const [phase, setPhase] = useState('idle')   // idle | flood | line | done
  const [pal, setPal]     = useState(null)
  const [reel, setReel]   = useState(0)
  const [saved, setSaved] = useState(false)
  const [slam, setSlam]   = useState(false)
  const [shareLabel, shareCard, sharing] = useShareCard()
  useEffect(() => { if (phase !== 'line') { setSlam(false); return } const t = setTimeout(() => setSlam(true), 420); return () => clearTimeout(t) }, [phase])
  const timers = useRef([])
  const clear  = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  const later  = (fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t) }

  useEffect(() => {
    const handler = async (e) => {
      const show = e.detail && e.detail.show
      if (!show || !show.id) return
      clear()
      window.__nextepWrapUntil = Date.now() + 22000
      window.__nextepFinaleActive = true
      const who = (e.detail && e.detail.user) || user
      setPal(null); setReel(Math.round((e.detail && e.detail.rating) || 0)); setSaved(false)
      if (show.poster_path) paletteFromPoster(show.poster_path).then(p => setPal(p)).catch(() => {})
      const stats = await buildStats(show, who)
      // The keepsake row itself is minted by useWatchlist.setStatus for EVERY
      // completion now (lib/keepsake.js) — this cinematic only contributes the
      // series rating below.
      setData({ show, rating: (e.detail && e.detail.rating) || 0, stats })
      logEvent('cinematic_shown', { kind: 'finalcut', show_id: show.id })
      setPhase('flood')
      later(() => setPhase('line'), 800)
      if (!who) later(() => finish(), 6000)
    }
    window.addEventListener('nextep:finalcut', handler)
    return () => { window.removeEventListener('nextep:finalcut', handler); clear() }
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  const finish = () => {
    clear()
    window.__nextepWrapUntil = 0
    window.__nextepFinaleActive = false
    window.dispatchEvent(new CustomEvent('nextep:wrap-done'))
    setPhase('done')
    setTimeout(() => { setData(null); setPhase('idle') }, 500)
  }

  const rateSeries = (n) => {
    if (saved || !data) return
    setReel(n); setSaved(true)
    try { wl?.rate?.(data.show.id, n) } catch (err) { /* best-effort */ }
    rateKeepsake(user?.id, data.show.id, n).catch(() => {})
    later(() => finish(), 1400)
  }

  if (phase === 'idle' || !data) return null
  const show = data.show
  const st   = data.stats || {}
  const top  = (pal && pal.raised) || 'var(--bg-raised)'
  const mid  = (pal && pal.bg) || 'var(--bg)'
  const run  = st.started && st.finished ? `${fmtDate(st.started)} → ${fmtDate(st.finished)}` : (st.finished ? fmtDate(st.finished) : null)
  const beat = (i) => ({ animation: 'nxFcUp 400ms cubic-bezier(0.22,1,0.36,1) both', animationDelay: `${i * 80}ms` })
  const line = phase === 'line'

  return (
    <div className="nx-finalcut" onClick={finish} style={{ position:'fixed', inset:0, zIndex:'var(--z-celebration)', fontFamily:'var(--font)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'0 20px', opacity: phase === 'done' ? 0 : 1, transition:'opacity 500ms ease' }}>
      <style>{'@keyframes nxFcUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}} @media (prefers-reduced-motion: reduce){.nx-finalcut *{animation-duration:1ms!important;transition-duration:1ms!important}}'}</style>
      <div aria-hidden="true" style={{ position:'absolute', inset:0, background:`linear-gradient(180deg, ${top} 0%, ${mid} 60%, var(--bg) 100%)`, opacity: phase === 'flood' || line ? 0.96 : 0, transform: phase === 'flood' || line ? 'scaleY(1)' : 'scaleY(0.28)', transformOrigin:'50% 0%', transition:'transform 600ms cubic-bezier(0.45,0,0.55,1), opacity 600ms cubic-bezier(0.45,0,0.55,1)' }} />
      {line && (
        <div onClick={e => e.stopPropagation()} style={{ position:'relative', display:'flex', flexDirection:'column', alignItems:'center', gap:28, maxWidth:420, width:'100%', textAlign:'center' }}>
          <div style={{ ...beat(0), display:'flex', justifyContent:'center', marginBottom:-8 }}><Clapboard size={64} watched={slam} accent="var(--amber)" idle="rgba(var(--photo-ink-rgb),0.7)" /></div>
          <div style={{ ...beat(0), fontFamily:'var(--font-mono)', fontSize:11, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--amber)' }}>Series finale</div>
          <div style={{ ...beat(1), fontFamily:'var(--font-serif)', fontStyle:'italic', fontWeight:400, fontSize:40, lineHeight:1.1, color:'#fff', letterSpacing:'-0.01em', marginTop:-12 }}>That’s a wrap.</div>
          <div style={{ ...beat(2), fontSize:14, color:'var(--text-muted)', marginTop:-14 }}>{show.name}</div>

          {user && (
            // Was a hand-rolled 5-button FilmReel picker (a different glyph, whole
            // numbers only) instead of the same ReelRating control every other
            // rating surface uses — a real inconsistency Jason flagged (2026-09-18).
            <div style={beat(3)} onClick={e => e.stopPropagation()}>
              <ReelRating value={reel} onChange={rateSeries} size={48} showLabel={false} readonly={saved} />
              <div style={{ fontSize:13, color:'var(--text-muted)', marginTop:12, textAlign:'center' }}>{saved ? `${REEL_LABELS[Math.round(reel*2)/2]?.label || ''} · saved to your keepsakes` : reel ? REEL_LABELS[Math.round(reel*2)/2]?.label : 'Rate the series'}</div>
            </div>
          )}

          <div style={{ ...beat(4), display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px 24px', width:'100%', maxWidth:320, textAlign:'left' }}>
            {[['Seasons', st.seasons], ['Episodes', st.episodes], ['Time well spent', hoursLabel(st.minutes, st.episodes)], ['Your run', run], ['Series completed', st.count ? `No. ${st.count}` : null]].filter(([, v]) => v != null && v !== '').map(([k, v]) => (
              <div key={k}>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--text-dim)', marginBottom:3 }}>{k}</div>
                <div style={{ fontSize:15, color:'var(--text)' }}>{v}</div>
              </div>
            ))}
          </div>

          <button disabled={sharing} aria-busy={sharing || undefined} onClick={e => { e.stopPropagation(); shareCard({ show, eyebrow: 'That’s a wrap', headline: show.name, line: `${st.seasons ? `${st.seasons} season${st.seasons === 1 ? '' : 's'}` : ''}${st.episodes ? ` · ${st.episodes} episodes` : ''}${reel ? ` · ${reel} reels` : ''}`.replace(/^ · /, '') }) }} style={{ ...beat(5), height: 44, padding: '0 20px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: sharing ? 'default' : 'pointer', opacity: sharing ? 0.7 : 1 }}>{shareLabel}</button>
          {!saved && <button onClick={finish} style={{ ...beat(5), background:'none', border:'none', color:'var(--text-dim)', fontSize:13, fontFamily:'var(--font)', cursor:'pointer', padding:'8px 4px' }}>Tap anywhere to continue</button>}
        </div>
      )}
    </div>
  )
}
